import { BLANK, NONE } from '@mona-roadmap/domain';
import type { FilterState, PersonRef } from '@mona-roadmap/domain';
import type { ColumnKey } from './columns';
import { formatDate } from './format';

export interface FilterLabel {
  key: keyof FilterState;
  text: string;
  /** Column whose editor this chip reopens (null for the global search). */
  column: ColumnKey | null;
}

const MULTI: Array<{ key: 'goals' | 'epics' | 'productAreas' | 'types' | 'pmOwners' | 'assignees' | 'sprints' | 'scopingStatuses' | 'statuses'; label: string; column: ColumnKey; people?: boolean; named?: 'goals' | 'epics' }> = [
  { key: 'goals', label: 'Product Goal', column: 'goal', named: 'goals' },
  { key: 'epics', label: 'Epic', column: 'epic', named: 'epics' },
  { key: 'productAreas', label: 'Product Area', column: 'productArea' },
  { key: 'types', label: 'Type', column: 'type' },
  { key: 'pmOwners', label: 'PM Owner', column: 'pmOwner', people: true },
  { key: 'assignees', label: 'Assignee', column: 'assignee', people: true },
  { key: 'sprints', label: 'Sprint', column: 'sprint' },
  { key: 'scopingStatuses', label: 'Scoping', column: 'scopingStatus' },
  { key: 'statuses', label: 'Status', column: 'status' },
];

/** One short chip per active filter, shown next to the search box. */
export interface NameLookup {
  goals: (id: string) => string | undefined;
  epics: (id: string) => string | undefined;
}

export function describeFilters(f: FilterState, people: PersonRef[], names?: NameLookup): FilterLabel[] {
  const out: FilterLabel[] = [];
  if (f.q.trim()) out.push({ key: 'q', text: `Search: "${f.q.trim()}"`, column: null });
  if (f.name.trim()) out.push({ key: 'name', text: `Name: "${f.name.trim()}"`, column: 'name' });
  for (const m of MULTI) {
    const values = f[m.key];
    if (values.length === 0) continue;
    if (values.length === 1 && values[0] === NONE) {
      out.push({ key: m.key, text: `${m.label}: none`, column: m.column });
      continue;
    }
    const blank = m.named === 'goals' ? 'No Product Goal' : m.named === 'epics' ? 'No Epic' : '(Blanks)';
    const shownNames = values.map((v) => (v === BLANK ? blank : m.people ? (people.find((p) => p.id === v)?.name ?? v) : m.named ? (names?.[m.named](v) ?? v) : v));
    const shown = shownNames.length > 2 ? `${shownNames.slice(0, 2).join(', ')} +${shownNames.length - 2}` : shownNames.join(', ');
    out.push({ key: m.key, text: `${m.label}: ${shown}`, column: m.column });
  }
  if (f.scopingDueFrom || f.scopingDueTo) {
    out.push({ key: 'scopingDueFrom', text: `Due: ${f.scopingDueFrom ? formatDate(f.scopingDueFrom) : '...'} to ${f.scopingDueTo ? formatDate(f.scopingDueTo) : '...'}`, column: 'scopingDue' });
  }
  return out;
}

/** The state patch that removes one chip. */
export function resetPatch(key: keyof FilterState): Partial<FilterState> {
  if (key === 'scopingDueFrom' || key === 'scopingDueTo') return { scopingDueFrom: null, scopingDueTo: null };
  if (key === 'q' || key === 'name') return { [key]: '' };
  return { [key]: [] } as Partial<FilterState>;
}
